import React, { useState } from 'react';
import { Sparkles, Database, Zap, Users, ArrowRight, CheckCircle } from 'lucide-react';

export default function TeaserLanding() {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    if (!email) return;

    setLoading(true); // ← 추가!

    try {
      const SCRIPT_URL = 'https://script.google.com/macros/s/AKfycbx0PyhTct6LoA7vkSvvZKwXP9RLKtbM89DPPC5sPfyDwzOJCszk5ugvjrz4CXRXuAgL/exec';

      const response = await fetch(SCRIPT_URL, {
        method: 'POST',
        mode: 'no-cors',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email })
      });

      setLoading(false); // ← 추가!
      setSubmitted(true);
      setTimeout(() => {
        setSubmitted(false);
        setEmail('');
      }, 3000);
    } catch (error) {
      console.error('Error:', error);
      setLoading(false); // ← 추가!
      setSubmitted(true);
      setTimeout(() => {
        setSubmitted(false);
        setEmail('');
      }, 3000);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
      {/* Hero Section */}
      <div className="relative overflow-hidden">
        {/* Animated background elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-40 -right-40 w-80 h-80 bg-purple-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse"></div>
          <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-blue-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse" style={{ animationDelay: '1s' }}></div>
        </div>

        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
          {/* Badge */}
          <div className="flex justify-center mb-8">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-purple-500/20 backdrop-blur-sm border border-purple-500/30 rounded-full text-purple-200 text-sm">
              <Sparkles className="w-4 h-4" />
              <span>Coming Soon</span>
            </div>
          </div>

          {/* Main Headline */}
          <div className="text-center mb-12">
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-white mb-6 leading-tight">
              테스트 데이터,
              <br />
              <span className="bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
                이제 프롬프트로
              </span>
            </h1>
            <p className="text-xl sm:text-2xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
              복잡한 테이블 관계 파악할 필요 없이,<br />
              AI에게 말하면 끝. 누구나 쉽게 만드는 테스트 데이터.
            </p>
          </div>

          {/* Demo Preview */}
          <div className="max-w-4xl mx-auto mb-12">
            <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700 rounded-2xl p-6 shadow-2xl">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-3 h-3 rounded-full bg-red-500"></div>
                <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                <div className="w-3 h-3 rounded-full bg-green-500"></div>
              </div>
              <div className="bg-slate-900/80 rounded-lg p-6 font-mono text-sm">
                <div className="flex items-start gap-3 mb-4">
                  <span className="text-purple-400">💬</span>
                  <span className="text-gray-300">"활성 사용자 10명 만들어줘, 각각 최근 주문 내역 3개씩"</span>
                </div>
                <div className="flex items-start gap-3 text-green-400">
                  <span>✨</span>
                  <div className="flex-1">
                    <div className="mb-2">INSERT INTO users ... (10 rows)</div>
                    <div className="mb-2">INSERT INTO orders ... (30 rows)</div>
                    <div className="text-gray-500">// 관련 테이블 자동 처리됨</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Problem → Solution */}
          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto mb-16">
            <div className="bg-red-500/10 backdrop-blur-sm border border-red-500/30 rounded-xl p-6">
              <h3 className="text-xl font-bold text-red-300 mb-4">😩 이런 경험 있으신가요?</h3>
              <ul className="space-y-3 text-gray-300">
                <li className="flex items-start gap-2">
                  <span className="text-red-400 mt-1">•</span>
                  <span>테이블 관계 파악하느라 시간 낭비</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-red-400 mt-1">•</span>
                  <span>기획자/프론트가 데이터 요청할 때마다 손수 작업</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-red-400 mt-1">•</span>
                  <span>FK 제약조건 에러로 삽질</span>
                </li>
              </ul>
            </div>

            <div className="bg-green-500/10 backdrop-blur-sm border border-green-500/30 rounded-xl p-6">
              <h3 className="text-xl font-bold text-green-300 mb-4">✨ 이제 이렇게 해결하세요</h3>
              <ul className="space-y-3 text-gray-300">
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-green-400 mt-0.5 flex-shrink-0" />
                  <span>프롬프트 입력만으로 즉시 생성</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-green-400 mt-0.5 flex-shrink-0" />
                  <span>비개발자도 직접 테스트 데이터 생성</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-green-400 mt-0.5 flex-shrink-0" />
                  <span>테이블 의존성 자동 해결</span>
                </li>
              </ul>
            </div>
          </div>

          {/* Key Features */}
          <div className="grid sm:grid-cols-3 gap-6 max-w-5xl mx-auto mb-16">
            <div className="bg-slate-800/30 backdrop-blur-sm border border-slate-700 rounded-xl p-6 text-center hover:border-purple-500/50 transition-all">
              <div className="w-14 h-14 bg-purple-500/20 rounded-xl flex items-center justify-center mx-auto mb-4">
                <Zap className="w-7 h-7 text-purple-400" />
              </div>
              <h3 className="text-lg font-bold text-white mb-2">AI 기반 생성</h3>
              <p className="text-gray-400 text-sm">자연어로 원하는 데이터 설명하면 자동 생성</p>
            </div>

            <div className="bg-slate-800/30 backdrop-blur-sm border border-slate-700 rounded-xl p-6 text-center hover:border-purple-500/50 transition-all">
              <div className="w-14 h-14 bg-blue-500/20 rounded-xl flex items-center justify-center mx-auto mb-4">
                <Database className="w-7 h-7 text-blue-400" />
              </div>
              <h3 className="text-lg font-bold text-white mb-2">스키마 자동 파악</h3>
              <p className="text-gray-400 text-sm">DB 구조 분석해서 관계 자동 처리</p>
            </div>

            <div className="bg-slate-800/30 backdrop-blur-sm border border-slate-700 rounded-xl p-6 text-center hover:border-purple-500/50 transition-all">
              <div className="w-14 h-14 bg-pink-500/20 rounded-xl flex items-center justify-center mx-auto mb-4">
                <Users className="w-7 h-7 text-pink-400" />
              </div>
              <h3 className="text-lg font-bold text-white mb-2">팀 협업 가능</h3>
              <p className="text-gray-400 text-sm">개발자가 아니어도 쉽게 사용</p>
            </div>
          </div>

          {/* CTA Section */}
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-white mb-4">
              출시 알림 받기
            </h2>
            <p className="text-gray-300 mb-8">
              얼리버드로 무료 크레딧을 드립니다
            </p>

            {!submitted ? (
              <div className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="이메일 주소 입력"
                  className="flex-1 px-6 py-4 bg-slate-800/50 border border-slate-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-purple-500 transition-all"
                  disabled={loading}
                />
                <button
                  onClick={handleSubmit}
                  disabled={loading || !email}
                  className="px-8 py-4 bg-gradient-to-r from-purple-500 to-pink-500 text-white font-semibold rounded-xl hover:from-purple-600 hover:to-pink-600 transition-all flex items-center justify-center gap-2 shadow-lg hover:shadow-purple-500/50 disabled:opacity-50 disabled:cursor-not-allowed min-w-[140px]"
                >
                  {loading ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      <span>전송중...</span>
                    </>
                  ) : (
                    <>
                      알림 받기
                      <ArrowRight className="w-5 h-5" />
                    </>
                  )}
                </button>
              </div>
            ) : (
              <div className="bg-green-500/20 border border-green-500/50 rounded-xl px-6 py-4 max-w-md mx-auto">
                <p className="text-green-300 font-semibold flex items-center justify-center gap-2">
                  <CheckCircle className="w-5 h-5" />
                  등록 완료! 출시 소식을 보내드릴게요
                </p>
              </div>
            )}

            <p className="text-gray-500 text-sm mt-4">
              스팸 없음. 출시 알림만 보내드립니다.
            </p>
          </div>

          {/* Social Proof */}
          <div className="mt-20 text-center">
            <p className="text-gray-400 mb-4">개발자들이 공감한 문제</p>
            <div className="flex flex-wrap justify-center gap-8 text-gray-500 text-sm">
              <div className="flex items-center gap-2">
                <span className="text-2xl">😤</span>
                <span>"매번 FK 에러로 삽질..."</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-2xl">⏰</span>
                <span>"테스트 데이터에만 1시간씩..."</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-2xl">🙏</span>
                <span>"기획자도 직접 만들게 해주세요"</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="border-t border-slate-800 py-8">
        <div className="max-w-6xl mx-auto px-4 text-center text-gray-500 text-sm">
          <p>© 2025 Test Data AI. Made with 💜 by solo developer</p>
        </div>
      </footer>
    </div>
  );
}